import { Fragment, useMemo, useState } from "react";
import { formatSecs, iAxios } from "../../../lib/utils";
import { Spinner } from "../../common/Helper";
import { useMilestone } from "../../../lib/hooks";
import classNames from "classnames";

const TIER_TONE = { High: 'good', Average: 'warn', Low: 'bad' };

// Lenses for ranking the team. Each re-weights the scoring components and the
// table re-sorts / re-tiers client-side from the data the backend already
// returned (normalised components + raw metrics) — no extra round-trip.
const DIMENSIONS = [
    { key: 'overall', label: 'Overall', blurb: 'Balanced view across all signals.',
        weights: { efficiency: 0.40, dev: 0.25, presence: 0.20, throughput: 0.15 } },
    { key: 'presence', label: 'Presence', blurb: 'In-time vs an 8h day and days present.',
        weights: { presence: 0.60, attendance: 0.40 } },
    { key: 'delivery', label: 'Delivery', blurb: 'Tasks committed & completed, delivered on time.',
        weights: { throughput: 0.45, committed: 0.25, onTime: 0.30 } },
    { key: 'efficiency', label: 'Efficiency', blurb: 'Estimated vs actual on completed tasks.',
        weights: { efficiency: 0.70, onTime: 0.30 } },
];

// Every employee is expected to have an 8h/day check-in. Presence is measured
// against this absolute target (not just relative to the team).
const EXPECTED_DAY_SEC = 8 * 3600;

const minmax = (v, mn, mx, neutral = 0.5) => {
    if (v == null || mx === mn) return neutral;
    return Math.min(Math.max((v - mn) / (mx - mn), 0), 1);
};

// Avg in-time shown against the 8h/day target.
const AvgIn = ({ secs }) => {
    const pct = Math.round((secs / EXPECTED_DAY_SEC) * 100);
    const tone = pct >= 100 ? 'good' : pct >= 70 ? 'warn' : 'bad';
    return <span title={`${pct}% of the 8h target`}>
        {formatSecs(secs)} <span className={`ds-tone-${tone}`} style={{ fontSize: '0.82em' }}>({pct}%)</span>
    </span>;
};

// Build a 0..1 component vector per user. efficiency/dev/presence/throughput are
// already team-normalised by the backend; committed/attendance we normalise here.
const buildComponents = (team) => {
    const touched = team.map(u => u.tasksTouched);
    const days = team.map(u => u.daysIn);
    const tMin = Math.min(...touched), tMax = Math.max(...touched);
    const dMin = Math.min(...days), dMax = Math.max(...days);
    return team.map(u => ({
        ...u,
        comp: {
            efficiency: u.components?.efficiency ?? 0.5,
            dev: u.components?.dev ?? 0,
            presence: u.components?.presence ?? 0,
            throughput: u.components?.throughput ?? 0,
            committed: minmax(u.tasksTouched, tMin, tMax),
            attendance: minmax(u.daysIn, dMin, dMax),
            onTime: u.onTimeRate == null ? 0.5 : u.onTimeRate,
        },
    }));
};

// Score by a weight map, then sort desc and assign rank + tertile tier.
const scoreBy = (users, weights) => {
    const scored = users.map(u => {
        let s = 0;
        for (const [k, w] of Object.entries(weights)) s += w * (u.comp[k] ?? 0);
        return { ...u, dimScore: Math.round(s * 100) };
    });
    scored.sort((a, b) => b.dimScore - a.dimScore);
    const n = scored.length;
    const highCut = Math.ceil(n / 3);
    const lowCut = n - Math.floor(n / 3);
    scored.forEach((u, i) => {
        u.dimRank = i + 1;
        u.dimTier = i < highCut ? 'High' : (i >= lowCut ? 'Low' : 'Average');
    });
    return scored;
};

const Tier = ({ tier }) => (
    <span className={`ds-badge ds-tone-${TIER_TONE[tier] || 'mute'}`}>{tier}</span>
);

// efficiency = estimate / actual : >=1 is at/under estimate (good)
const Efficiency = ({ value }) => {
    if (value == null) return <span className="ds-tone-mute">—</span>;
    const tone = value >= 1 ? 'good' : value >= 0.7 ? 'warn' : 'bad';
    return <span className={`ds-tone-${tone}`}>{value}×</span>;
};

const Variance = ({ pct }) => {
    if (pct == null) return <span className="ds-tone-mute">—</span>;
    // positive variance = actual exceeded estimate (over budget)
    const tone = pct <= 0 ? 'good' : pct <= 25 ? 'warn' : 'bad';
    return <span className={`ds-tone-${tone}`}>{pct > 0 ? '+' : ''}{pct}%</span>;
};

const FlagList = ({ flags }) => {
    if (!flags || !flags.length) return null;
    return <span style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>
        {flags.map(f => <span key={f} className="ds-badge ds-tone-mute" style={{ fontSize: '0.72em' }}>{f}</span>)}
    </span>;
};

const TaskBreakdown = ({ tasks }) => {
    if (!tasks || !tasks.length) {
        return <div className="ds-tone-mute" style={{ padding: 'var(--ds-space-3)' }}>No task-linked check-ins in this range.</div>;
    }
    return <div className="ds-table-wrap" style={{ margin: 'var(--ds-space-2) 0' }}>
        <table className="ds-table is-striped">
            <thead>
                <tr>
                    <th>Task</th><th>State</th><th>Estimate</th><th>Actual</th>
                    <th>Variance</th><th>Dev</th><th>On time</th>
                </tr>
            </thead>
            <tbody>
                {tasks.map(t => (
                    <tr key={t.ticketId}>
                        <td style={{ maxWidth: '320px' }}>
                            {t.url ? <a href={t.url} target="_blank" rel="noreferrer">{t.title}</a> : t.title}
                        </td>
                        <td>{t.state || '—'}</td>
                        <td>{t.hasEstimate ? formatSecs(t.estimateSec) : <span className="ds-tone-mute">none</span>}</td>
                        <td>{formatSecs(t.actualSec)}</td>
                        <td><Variance pct={t.variancePct} /></td>
                        <td>{t.devSec ? formatSecs(t.devSec) : '—'}</td>
                        <td>{t.onTime == null ? '—' : (t.onTime ? <span className="ds-tone-good">yes</span> : <span className="ds-tone-bad">no</span>)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>;
};

export const InsightsDashboard = () => {
    const milestone = useMilestone();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(null); // username whose tasks are shown
    const [dimension, setDimension] = useState('overall');

    const activeDim = DIMENSIONS.find(d => d.key === dimension) || DIMENSIONS[0];
    // Re-score & re-rank the team for the active lens, client-side.
    const scored = useMemo(() => {
        if (!report?.team?.length) return [];
        const dim = DIMENSIONS.find(d => d.key === dimension) || DIMENSIONS[0];
        return scoreBy(buildComponents(report.team), dim.weights);
    }, [report, dimension]);
    const highCount = scored.filter(u => u.dimTier === 'High').length;
    const lowCount = scored.filter(u => u.dimTier === 'Low').length;

    const analyse = () => {
        if (!milestone) return;
        setLoading(true);
        setError(null);
        iAxios.post('/api/v1/admin/analyse-performance', {
            start: milestone.start,
            end: milestone.end,
        }).then(resp => {
            if (resp.data?.success) {
                setReport(resp.data.data);
                setExpanded(null);
            } else {
                setError(resp.data?.message || 'Failed to analyse');
            }
        }).catch(ex => {
            setError(ex.response?.data?.message || ex.message);
        }).finally(() => setLoading(false));
    };

    const toggle = (username) => setExpanded(prev => (prev === username ? null : username));

    return <div className="ds-panel" style={{ padding: 'var(--ds-space-5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ds-space-4)', flexWrap: 'wrap' }}>
            <div style={{ marginRight: 'auto' }}>
                <h3 style={{ margin: 0 }}>Performance Analysis</h3>
                <div className="ds-tone-mute" style={{ fontSize: '0.85em' }}>
                    {milestone ? `${milestone.start} → ${milestone.end}` : 'Loading period…'}
                </div>
            </div>
            <button className="ds-btn is-primary" onClick={analyse} disabled={loading || !milestone}>
                {loading ? 'Analysing…' : 'Analyse'}
            </button>
        </div>

        {report && !loading && (
            <div style={{ marginTop: 'var(--ds-space-4)' }}>
                <div className="ds-tabs">
                    {DIMENSIONS.map(d => (
                        <button key={d.key} type="button"
                            className={classNames('ds-tab', { 'is-active': dimension === d.key })}
                            onClick={() => setDimension(d.key)}>
                            {d.label}
                        </button>
                    ))}
                </div>
                <div className="ds-tone-mute" style={{ fontSize: '0.8em', marginTop: 'var(--ds-space-2)' }}>
                    {activeDim.blurb} Weights: {Object.entries(activeDim.weights)
                        .map(([k, w]) => `${Math.round(w * 100)}% ${k}`).join(' + ')}.
                    {' '}Presence is scored against the 8h/day target.
                </div>
            </div>
        )}

        {error && <div className="ds-tone-bad" style={{ marginTop: 'var(--ds-space-4)' }}>{error}</div>}
        {loading && <Spinner />}

        {report && !loading && (
            report.count === 0
                ? <div className="issue-empty" style={{ marginTop: 'var(--ds-space-5)' }}>No activity found in this period.</div>
                : <div style={{ marginTop: 'var(--ds-space-5)' }}>
                    <div className="ds-table-wrap">
                        <table className="ds-table is-striped">
                            <thead>
                                <tr>
                                    <th>#</th><th>Employee</th><th>Tier</th><th>Score</th>
                                    <th>Avg In / 8h</th><th>Dev %</th><th>Efficiency</th>
                                    <th>Done/Committed</th><th>On-time</th><th>Flags</th>
                                </tr>
                            </thead>
                            <tbody>
                                {scored.map(u => (
                                    <Fragment key={u.username}>
                                        <tr onClick={() => toggle(u.username)}
                                            style={{ cursor: 'pointer' }}
                                            title="Click to see task breakdown">
                                            <td>{u.dimRank}</td>
                                            <td><span className="admin-name">{u.name || u.username}</span></td>
                                            <td><Tier tier={u.dimTier} /></td>
                                            <td><strong>{u.dimScore}</strong></td>
                                            <td><AvgIn secs={u.avgInSec} /></td>
                                            <td>{u.devPct}%</td>
                                            <td><Efficiency value={u.efficiency} /></td>
                                            <td>{u.tasksCompleted}/{u.tasksTouched}</td>
                                            <td>{u.onTimeRate == null ? '—' : `${Math.round(u.onTimeRate * 100)}%`}</td>
                                            <td><FlagList flags={u.flags} /></td>
                                        </tr>
                                        {expanded === u.username && (
                                            <tr>
                                                <td colSpan={10} style={{ background: 'var(--ds-surface-alt)' }}>
                                                    <TaskBreakdown tasks={u.tasks} />
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="ds-tone-mute" style={{ fontSize: '0.8em', marginTop: 'var(--ds-space-3)' }}>
                        {activeDim.label} lens: {highCount} high · {lowCount} low ·
                        {' '}{report.count} analysed. Click a row for the per-task estimate-vs-actual breakdown.
                    </div>
                </div>
        )}
    </div>;
};
